import get from 'lodash/get'

import {
  AMERICAN_EXPRESS_ICON,
  BANK,
  DINERS_CLUB_ICON,
  DISCOVER_ICON,
  GENERIC_CARD_ICON,
  MASTERCARD_ICON,
  PAYPAL_ICON,
  TREVIPAY_ICON,
  VISA_DEBIT_ICON,
  VISA_ICON,
} from 'components/Core/Icon/Icon'

export const IMAGE_WIDTH = 330
export const IMAGE_HEIGHT = 450
export const BADGE_WIDTH = 50
export const BADGE_HEIGHT = 50
export const BADGE_SUFFIX = `.${BADGE_WIDTH}x${BADGE_HEIGHT}.`
export const IMAGE_CODE = 'IMAGE'
export const BADGE_CODE = 'BADGE'
export const PREVIEW_IMAGE_CODE = 'PREVIEW_IMAGE'
const MAGNIFICATION_FACTOR = 2.5
const PREVIEW_IMAGE_WIDTH = 450
const PREVIEW_IMAGE_HEIGHT = 600
const LARGE_IMAGE_WIDTH = 660
const LARGE_IMAGE_HEIGHT = 900
const LARGE_IMAGE_DIMENSION = `${LARGE_IMAGE_WIDTH}x${LARGE_IMAGE_HEIGHT}`
const ZOOM_IMAGE_WIDTH = 1650
const ZOOM_IMAGE_HEIGHT = 2250
const ZOOM_IMAGE_DIMENSION = `${ZOOM_IMAGE_WIDTH}x${ZOOM_IMAGE_HEIGHT}`
const THUMBNAIL_IMAGE_WIDTH = 136
const THUMBNAIL_IMAGE_HEIGHT = 181
const THUMBNAIL_IMAGE_DIMENSION = `${THUMBNAIL_IMAGE_WIDTH}x${THUMBNAIL_IMAGE_HEIGHT}`

export const isVideo = (mediaSource) =>
  mediaSource && mediaSource.includes('mp4')

/**
 * Sizes image to badge size or previewImage size
 * If imageType passed in does not match BADGE_CODE or PREVIEW_IMAGE_CODE,
 * will remove previewImage sizing if already applied else do nothing
 */
export const getSizedImage = (imageUrl, imageType) => {
  const isBadge = imageType === BADGE_CODE
  const isPreviewImage = imageType === PREVIEW_IMAGE_CODE

  if (isBadge && imageUrl) {
    const fileExtension = imageUrl.split('.').pop()
    return imageUrl.replace(`.${fileExtension}`, BADGE_SUFFIX + fileExtension)
  } else if (isPreviewImage && imageUrl) {
    return getPreviewImageSizedUrl(imageUrl)
  } else if (!isBadge && imageUrl) {
    return imageUrl.replace('.450x600.w', '')
  }
  return ''
}

export const getMagnifiedDimensions = (width, height) => {
  return {
    width: Math.round(width * MAGNIFICATION_FACTOR),
    height: Math.round(height * MAGNIFICATION_FACTOR),
  }
}

/**
 * Sizes image to size/dimension specified
 * If no dimension specified, defaults to 220x300
 * For further info about supported dimensions, see: https://github.com/joor/aws-lambda/blob/master/src/imgixRedirect/constants.js#L3
 */
export const getSizedImageUrl = (url, dimension = '220x300') => {
  if (!url) {
    return url
  }
  const imageUrl = url
    .replace(/\d+[Xx]\d+\.w./, '')
    .split('.')
    .slice(0, -1)
  const ext = url.split('.').pop()
  return `${imageUrl.join('.')}.${dimension}.w.${ext}`
}

/**
 * Encodes the image name in the URL to ensure it is safe for use in URLs
 * This function will encode the image name part of the URL
 * while keeping the rest of the URL intact
 * It will also handle cases where the image name has multiple parts (e.g., with dots)
 * and ensure the extension remains unchanged.
 */
export const encodeURIComponentImageName = (url) => {
  if (!url) {
    return url
  }

  const imageParts = url.split('/')
  const imageNameParts = imageParts.pop().split('.')
  const ext = imageNameParts.pop()
  if (imageNameParts.length === 0) {
    return url
  }

  return `${imageParts.join('/')}/${encodeURIComponent(
    imageNameParts.length === 1 ? imageNameParts : imageNameParts.join('.'),
  )}.${ext}`
}

/**
 * Returns the URL to use for an image that will be drawn onto a canvas (e.g. to
 * crop it). `canvas.toBlob` throws if the canvas was tainted by an image fetched
 * without CORS, so the source must be requested with `crossOrigin` AND come back
 * with `Access-Control-Allow-Origin`.
 *
 * The CDN only sends that header for paths carrying a resolution suffix
 * (`.<W>x<H>.w.<ext>`), because those are routed through the imgix redirect that
 * adds it — a raw upload URL gets no CORS headers outside the CDN's origin
 * allowlist, which is why cropping broke in local dev. The query string has no
 * effect on this, so a cache buster does not help.
 */
export const getCropImageUrl = (url) => {
  if (!url || url.startsWith('data:') || url.startsWith('blob:')) {
    return url
  }
  return getSizedImageUrl(url, ZOOM_IMAGE_DIMENSION)
}

/**
 * Sizes image to preview image size
 */
export const getPreviewImageSizedUrl = (url) =>
  getSizedImageUrl(url, `${PREVIEW_IMAGE_WIDTH}x${PREVIEW_IMAGE_HEIGHT}`)

/**
 * Sizes image to the large product image size (660x900, no crop)
 */
export const getLargeImageSizedUrl = (url) =>
  getSizedImageUrl(url, LARGE_IMAGE_DIMENSION)

/**
 * Sizes image to the magnified/zoom size (1650x2250, no crop)
 */
export const getZoomImageSizedUrl = (url) =>
  getSizedImageUrl(url, ZOOM_IMAGE_DIMENSION)

/**
 * Sizes image to thumbnail size (120x160, no crop)
 */
export const getThumbnailImageSizedUrl = (url) =>
  getSizedImageUrl(url, THUMBNAIL_IMAGE_DIMENSION)

export const getIconNameForCard = (brandName = '') => {
  const lowerCaseBrand = brandName.toLowerCase()
  return (
    {
      'american express': AMERICAN_EXPRESS_ICON,
      amex: AMERICAN_EXPRESS_ICON,
      'diners club': DINERS_CLUB_ICON,
      diners: DINERS_CLUB_ICON,
      discover: DISCOVER_ICON,
      disc: DISCOVER_ICON,
      mastercard: MASTERCARD_ICON,
      mc: MASTERCARD_ICON,
      paypal: PAYPAL_ICON,
      trevipay: TREVIPAY_ICON,
      visa: VISA_ICON,
      debit: VISA_DEBIT_ICON,
      bank: BANK,
    }[lowerCaseBrand] || GENERIC_CARD_ICON
  )
}

export const getImgixCompressedUrl = ({
  image,
  quality = 35,
  flag = false,
}) => {
  if (!image) {
    return image
  }
  if (isVideo(image)) {
    return image
  }
  const isProd = image.includes('https://cdn.jooraccess.com')
  if (!isProd) {
    return image
  }
  if (!flag) {
    return image
  }

  const imagePath = image.split('https://cdn.jooraccess.com')[1]
  return `https://joor-prod.imgix.net${imagePath}?q=${quality}`
}

// TODO: All of this can be done with prop + classNames
// Not against doing things this way but the reason for the todo
// is this is an inconsistent pattern from the rest of passport.
export const generatePassportImageStyle = (options) => {
  let customImageStyle = {}
  if (get(options, 'fillContainer', false)) {
    customImageStyle = {
      ...customImageStyle,
      objectFit: 'cover',
      width: '100%',
      height: '100%',
      zIndex: -1,
    }
  }
  if (get(options, 'isGrayscale', false)) {
    customImageStyle = {
      ...customImageStyle,
      filter: 'grayscale(100%)',
      opacity: 0.8,
    }
  }
  if (get(options, 'isStyleStory', false)) {
    customImageStyle = {
      ...customImageStyle,
      objectFit: 'cover',
      width: '100%',
      height: '100%',
      position: 'relative',
      zIndex: '-1',
    }
  }
  return customImageStyle
}

export const formatHexColorCode = (hexColorCode) => {
  if (!hexColorCode) {
    return hexColorCode
  }
  return hexColorCode.trim() && hexColorCode.trim()[0] !== '#'
    ? `#${hexColorCode.trim()}`
    : hexColorCode.trim()
}

export const MediaType = {
  UNKNOWN: 'UNKNOWN',
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
}
